// Erasures (D83): every deletion of an organization and erasure of a person, open ones first,
// what runs when and what failed. An app user (D92), for a person who asked Apsio rather than the
// app's maker: the project and the user.hash, a day's grace. Every step is in both audit logs.
// Console D's list (APS-412 G1, admin-d.md sheet D): "Apagar um usuário do app" in the head opens
// its form window under it, the filter is one chip select, and each row says its state in a word
// with its one action after it (a failed one that can still be abandoned: the ⋯ menu), whose
// form opens right under the row. On a phone the target and its state come before the scroll.
import { PageHead, SelectChip } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Fragment, useId, useRef, useState } from 'react'
import {
  abortErasure,
  cancelErasure,
  type Erasure,
  erasuresQuery,
  retryErasure,
  STOP_CATEGORIES,
  type StopCategory,
  scheduleAppUserErasure,
  useAdminMutation,
} from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import { DataTable, IdText, RowMenu, StatusWord, shortId } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { IRREVERSIBLE, KIND_KEY } from './erasureParts'

export const ERASURE_FILTERS = ['open', 'done', 'canceled', 'failed'] as const
export type ErasureFilter = (typeof ERASURE_FILTERS)[number]

const FILTER_KEY: Record<ErasureFilter, MessageId> = {
  open: 'erasure.filter.open',
  done: 'erasure.filter.done',
  canceled: 'erasure.filter.canceled',
  failed: 'erasure.filter.failed',
}

/** A project's id, as the API checks it (the hash is checked there too). */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

type RowAction = 'cancel' | 'retry' | 'abort'

/** What staff can do with a row: cancel a scheduled one, retry or abandon a failed one. */
function rowActions(e: Erasure): RowAction[] {
  // One of a person's organizations is canceled or abandoned with the person.
  if (e.parent_id) return []
  if (e.status === 'scheduled') return ['cancel']
  if (e.status !== 'failed') return []
  const steps = (e.steps ?? {}) as Record<string, unknown>
  // After a step that deletes for good, a failed erasure is retried, never abandoned.
  return IRREVERSIBLE.some((step) => step in steps) ? ['retry'] : ['retry', 'abort']
}

const ACTION: Record<RowAction, { label: MessageId; title: MessageId; confirm: MessageId }> = {
  cancel: {
    label: 'erasure.row.cancel',
    title: 'erasure.cancel',
    confirm: 'erasure.cancelConfirm',
  },
  retry: { label: 'erasure.row.retry', title: 'erasure.retry', confirm: 'erasure.retryConfirm' },
  abort: { label: 'erasure.row.abort', title: 'erasure.abort', confirm: 'erasure.abortConfirm' },
}

const categoryOf = (form: FormData) => (form.get('category') ?? 'other') as StopCategory

/** Why it is stopped, as the email to the customer says it (the reason stays internal). */
function CategoryField() {
  const { t } = useI18n()
  const id = useId()
  return (
    <div className="field-d">
      <label className="lbl" htmlFor={id}>
        {t('erasure.category')}
      </label>
      <select id={id} name="category" className="select" defaultValue="withdrawn">
        {STOP_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {t(`erasure.category.${c}`)}
          </option>
        ))}
      </select>
    </div>
  )
}

/** A row's action, in its form window under the row: the category to stop, and the Motivo. */
function RowActionForm({
  erasure,
  action,
  onClose,
}: {
  erasure: Erasure
  action: RowAction
  onClose: () => void
}) {
  const { t } = useI18n()
  const refresh = [['erasures']]
  const cancel = useAdminMutation(cancelErasure, refresh)
  const retry = useAdminMutation(retryErasure, refresh)
  const abort = useAdminMutation(abortErasure, refresh)
  const a = ACTION[action]
  return (
    <ActionForm
      open
      onClose={onClose}
      label={t(a.title)}
      confirm={t(a.confirm)}
      reasonHint={t('erasure.reasonHint')}
      noEmails
      onSubmit={(reason, form) =>
        action === 'retry'
          ? retry.mutateAsync({ erasureId: erasure.id, reason })
          : (action === 'cancel' ? cancel : abort).mutateAsync({
              erasureId: erasure.id,
              reason,
              category: categoryOf(form),
            })
      }
    >
      <div className="wide dim">
        {t(KIND_KEY[erasure.kind])} <IdText id={erasure.subject_id} />
      </div>
      {action === 'retry' ? (
        <p className="wide dim">{t('erasure.failedNow', { error: erasure.error ?? '' })}</p>
      ) : (
        <CategoryField />
      )}
    </ActionForm>
  )
}

/** Erasing one app user of a project (D92): the project's id, their user.hash typed twice. */
function AppUserErasureForm({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useI18n()
  const schedule = useAdminMutation(scheduleAppUserErasure, [['erasures']])
  const ids = { project: useId(), hash: useId(), again: useId() }
  const hash = useRef<HTMLInputElement>(null)
  const again = useRef<HTMLInputElement>(null)
  // The same hash, typed again: the browser holds the submit until both match.
  const check = () =>
    again.current?.setCustomValidity(
      again.current.value.trim() === hash.current?.value.trim()
        ? ''
        : t('erasure.appUser.hashMismatch'),
    )
  return (
    <ActionForm
      open
      onClose={onClose}
      label={t('erasure.appUser.title')}
      confirm={t('erasure.appUser.confirm')}
      reasonHint={t('erasure.reasonHint')}
      noEmails
      danger
      onSubmit={async (reason, form) => {
        await schedule.mutateAsync({
          projectId: String(form.get('project') ?? '').trim(),
          hash: String(form.get('hash') ?? '').trim(),
          reason,
        })
        onDone()
      }}
    >
      <p className="wide dim">{t('erasure.appUser.about')}</p>
      <div className="field-d">
        <label className="lbl" htmlFor={ids.project}>
          {t('erasure.appUser.project')}
        </label>
        <input
          id={ids.project}
          name="project"
          className="input mono"
          translate="no"
          required
          autoComplete="off"
          pattern={UUID.source.slice(1, -1)}
        />
      </div>
      <div className="field-d">
        <label className="lbl" htmlFor={ids.hash}>
          {t('erasure.appUser.hash')}
        </label>
        <input
          ref={hash}
          id={ids.hash}
          name="hash"
          className="input mono"
          translate="no"
          required
          autoComplete="off"
          maxLength={256}
          onInput={check}
          aria-describedby={`${ids.hash}-hint`}
        />
        <span className="hint" id={`${ids.hash}-hint`}>
          {t('erasure.appUser.hashHint')}
        </span>
      </div>
      <div className="wide field-d">
        <label className="lbl" htmlFor={ids.again}>
          {t('erasure.appUser.hashAgain')}
        </label>
        <input
          ref={again}
          id={ids.again}
          className="input mono"
          translate="no"
          required
          autoComplete="off"
          maxLength={256}
          onInput={check}
        />
      </div>
    </ActionForm>
  )
}

/** Every erasure, open ones first: what runs when, and what failed. */
export function ErasuresPage() {
  const { t, dateTime } = useI18n()
  const search = useSearch({ strict: false }) as { status?: ErasureFilter }
  const status = search.status ?? 'open'
  const navigate = useNavigate()
  const list = useQuery(erasuresQuery(status))
  const [appUser, setAppUser] = useState(false)
  const [scheduled, setScheduled] = useState(false)
  const [acting, setActing] = useState<{ erasure: Erasure; action: RowAction } | null>(null)
  return (
    <main id="main" className="pg g1-list">
      <PageHead
        title={t('erasure.title')}
        meta={t('erasure.lede')}
        actions={
          appUser ? null : (
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                setScheduled(false)
                setAppUser(true)
              }}
            >
              {t('erasure.appUser.action')}
            </button>
          )
        }
      />
      <div className="g1-body">
        {appUser ? (
          <AppUserErasureForm onClose={() => setAppUser(false)} onDone={() => setScheduled(true)} />
        ) : null}
        {scheduled ? (
          <p role="status" className="g1-note">
            {t('erasure.appUser.scheduled')}
          </p>
        ) : null}
        {/* The list in a card, its filter in the card's head (brand v2, as the console's lists). */}
        <div className="sec g1-card">
          <div className="sec-h g1-card-h g1-filters">
            <SelectChip
              label={t('erasure.filter')}
              value={status}
              onChange={(s) =>
                navigate({
                  to: '/erasures',
                  search: s === 'open' ? {} : { status: s as ErasureFilter },
                })
              }
            >
              {ERASURE_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {t(FILTER_KEY[s])}
                </option>
              ))}
            </SelectChip>
          </div>
          {list.isPending ? (
            <Loading />
          ) : list.isError ? (
            <ErrorState error={list.error} retry={() => list.refetch()} />
          ) : list.data.length === 0 ? (
            <Empty title={t('erasure.none')} />
          ) : (
            <DataTable label={t('erasure.title')} minWidth={760}>
              <thead>
                <tr>
                  <th scope="col">{t('erasure.col.target')}</th>
                  <th scope="col">{t('erasure.col.status')}</th>
                  <th scope="col">
                    <span className="visually-hidden">{t('erasure.col.actions')}</span>
                  </th>
                  <th scope="col">{t('erasure.col.requested')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((e) => {
                  const actions = rowActions(e)
                  const open = (action: RowAction) => setActing({ erasure: e, action })
                  const editing = acting?.erasure.id === e.id ? acting : null
                  // The id is the link (no copy on a link): an organization's or a person's page.
                  const id =
                    e.kind === 'organization' && e.status !== 'done' ? (
                      <Link to="/orgs/$orgId" params={{ orgId: e.subject_id }}>
                        <IdText id={e.subject_id} copy={false} />
                      </Link>
                    ) : e.kind === 'user' && e.status !== 'done' ? (
                      <Link to="/users/$userId/erase" params={{ userId: e.subject_id }}>
                        <IdText id={e.subject_id} copy={false} />
                      </Link>
                    ) : (
                      <IdText id={e.subject_id} />
                    )
                  return (
                    <Fragment key={e.id}>
                      <tr>
                        <td>
                          {t(KIND_KEY[e.kind])} {id}
                          {e.subject_key ? (
                            <span className="mono dim" translate="no">
                              {' · '}
                              {e.subject_key}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <StatusWord
                            kind="erasure"
                            status={e.status}
                            until={e.status === 'scheduled' ? dateTime(e.execute_after) : undefined}
                          />
                          {e.status === 'done' || e.status === 'canceled' ? (
                            <span className="dim">
                              {' · '}
                              {dateTime(e.done_at ?? e.canceled_at ?? e.execute_after)}
                            </span>
                          ) : null}
                          {e.error ? (
                            <span className="mono dim" translate="no">
                              {' · '}
                              {e.error}
                            </span>
                          ) : null}
                        </td>
                        <td className="act">
                          {actions.length === 1 && actions[0] ? (
                            <button
                              type="button"
                              className="btn ghost sm"
                              aria-expanded={editing?.action === actions[0]}
                              onClick={() => open(actions[0] as RowAction)}
                            >
                              {t(ACTION[actions[0]].label)}
                            </button>
                          ) : actions.length > 1 ? (
                            <RowMenu
                              label={t('erasure.row.menu', { id: shortId(e.subject_id) })}
                              items={actions.map((a) => ({
                                label: t(ACTION[a].label),
                                onSelect: () => open(a),
                              }))}
                            />
                          ) : null}
                        </td>
                        <td>
                          {t(e.requester_kind === 'org' ? 'erasure.by.org' : 'erasure.by.staff')}
                          <span className="dim" translate="no">
                            {' · '}
                            {e.reason}
                          </span>
                        </td>
                      </tr>
                      {editing ? (
                        // The row's form, right under its row (as the cost model's edit).
                        <tr className="edit">
                          <td colSpan={4}>
                            <RowActionForm
                              key={editing.action}
                              erasure={e}
                              action={editing.action}
                              onClose={() => setActing(null)}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </DataTable>
          )}
        </div>
      </div>
    </main>
  )
}
